import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const ORIGIN = "https://mylimya.com";

function upsertMeta(name, content) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (content === null || content === undefined || content === "") {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Imperatively set page SEO. `canonical` and `robots` are always explicitly
// managed (pass null to clear canonical). Used by SeoManager and by dynamic
// pages such as the public Pro profile.
export function setSeo({ title, description, canonical, robots } = {}) {
  if (title) document.title = title;
  if (description !== undefined) upsertMeta("description", description);
  if (robots !== undefined) upsertMeta("robots", robots);
  if (canonical !== undefined) upsertCanonical(canonical);
}

const DEFAULT_TITLE = "LIMYA — The neighborhood bulletin board, reimagined.";
const DEFAULT_DESC =
  "LIMYA — your neighborhood's pros, pinned to one board. Discover local businesses your neighbors already trust.";

// Indexable public pages -> title/description (canonical is derived from path).
const PUBLIC_PAGES = {
  "/": { title: DEFAULT_TITLE, description: DEFAULT_DESC },
  "/search": {
    title: "Browse local pros | LIMYA",
    description: "Browse trusted local professionals your neighbors already use on LIMYA.",
  },
  "/learn": {
    title: "How LIMYA works | LIMYA",
    description: "Learn how LIMYA helps you find, save, and organize trusted local pros.",
  },
  "/faq": {
    title: "Frequently asked questions | LIMYA",
    description: "Answers to common questions about finding and saving local pros on LIMYA.",
  },
  "/contact": { title: "Contact LIMYA | LIMYA", description: "Get in touch with the LIMYA team." },
  "/legal": {
    title: "Legal center | LIMYA",
    description: "LIMYA legal center — terms of use, privacy policy, and membership terms.",
  },
  "/legal/terms": { title: "Terms of Use | LIMYA", description: "The terms of use for LIMYA." },
  "/legal/privacy": { title: "Privacy Policy | LIMYA", description: "How LIMYA handles your data and privacy." },
  "/legal/membership": { title: "Membership Terms | LIMYA", description: "LIMYA membership terms." },
  "/legal/pro-terms": { title: "Professional Terms | LIMYA", description: "Terms for professionals listed on LIMYA." },
};

// Query params on /search that must NOT create unlimited indexable URLs.
const SEARCH_PARAM_KEYS = [
  "query", "q", "category", "city", "zip", "radius", "verified", "pro_plus", "min_rating", "sort",
];

function normalize(pathname) {
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
}

// Centralised, route-aware SEO controller. Sets title, meta description,
// canonical and robots on every navigation. Public Pro profiles set their own
// (dynamic) SEO inside ProProfile, so this skips /pro/*.
export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const path = normalize(location.pathname);

    // Public Pro profiles own their dynamic SEO.
    if (path.startsWith("/pro/")) return;

    // /search: base page is indexable; any filter/param state canonicalises to
    // the base and is set noindex so we never create unlimited thin URLs.
    if (path === "/search") {
      const sp = new URLSearchParams(location.search);
      const hasParams = SEARCH_PARAM_KEYS.some((k) => sp.get(k));
      const meta = PUBLIC_PAGES["/search"];
      setSeo({
        title: meta.title,
        description: meta.description,
        canonical: `${ORIGIN}/search`,
        robots: hasParams ? "noindex, follow" : "index, follow",
      });
      return;
    }

    const meta = PUBLIC_PAGES[path];
    if (meta) {
      setSeo({
        title: meta.title,
        description: meta.description,
        canonical: `${ORIGIN}${path}`,
        robots: "index, follow",
      });
      return;
    }

    // Everything else — auth, account, dashboard, admin, boards, shared boards,
    // neighbor/client profiles, and unknown/404 URLs: publicly reachable but
    // must never appear in search. noindex (so Google can see it), no canonical.
    setSeo({
      title: DEFAULT_TITLE,
      description: DEFAULT_DESC,
      canonical: null,
      robots: "noindex, follow",
    });
  }, [location.pathname, location.search]);

  return null;
}
